import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sparkles, Loader2, Wand2, X, Check, Trash2, Plus, AlertTriangle, RefreshCw, Edit3, Link2, Unlink, Search, Layers, Package } from "lucide-react";
import { toast } from "sonner";
import StockPickerModal from "@/components/StockPickerModal";
import NewStockCardModal from "@/components/NewStockCardModal";

// Türkçe-farkında büyük harf çevirimi (i→İ, ı→I). JS'in native toUpperCase'i "Lacivert"→"LACIVERT" yapıyor (yanlış).
const trUpper = (s = "") => (s || "").toString().replace(/i/g, "İ").replace(/ı/g, "I").toUpperCase();

/**
 * AiProductDraftDialog — AI Ürün Oluşturucu Faz 1 (Ön İzleme + Onay Akışı)
 *
 * Akış: prompt → LLM parse → draft (backend'de saklanır, DB'ye YAZILMAZ) →
 *       kullanıcı taslağı düzenler (isim/kod/renk/beden/varyant/BOM/operasyon)
 *       → "Onayla ve Ürünü Oluştur" → gerçek ürün + varyant + reçete kaydı.
 * "Vazgeç" derse draft silinir, gerçek DB'ye tek satır bile yazılmamış olur.
 */
export default function AiProductDraftDialog({ open, onClose, onCreated, initialPrompt = "" }) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [draft, setDraft] = useState(null);         // backend'den gelen draft
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");

  // Reset when dialog is closed
  useEffect(() => {
    if (!open) {
      setPrompt(initialPrompt || "");
      setDraft(null);
      setBusy(false);
      setSaving(false);
      setConfirmOpen(false);
      setDiscardConfirm(false);
      setSaveTemplate(false);
      setTemplateName("");
    }
  }, [open, initialPrompt]);

  // BUG FIX (iter39): initialPrompt Copilot event ile değişirse ve dialog o esnada
  // open oluyorsa, prompt eski değerinde kalıyordu. Open → true geçişinde ve draft
  // henüz yokken, yeni initialPrompt ile senkronize et.
  useEffect(() => {
    if (open && !draft && initialPrompt) {
      setPrompt(initialPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPrompt]);

  // Generate the draft
  const generate = async () => {
    const p = prompt.trim();
    if (!p) return toast.error("Lütfen ürün açıklamasını yazın");
    setBusy(true);
    try {
      const { data } = await api.post("/copilot/product-draft", { prompt: p });
      setDraft(data);
      toast.success("Ürün Taslağı Hazır — inceleyip onaylayabilirsiniz");
    } catch (e) {
      const msg = formatApiError(e);
      toast.error(typeof msg === "string" ? msg : "AI taslak üretemedi");
    } finally {
      setBusy(false);
    }
  };

  // PATCH draft on any edit — race korumalı (son isteğin cevabı kazansın)
  // BUG FIX (iter39): per-keystroke PATCH'ler sırasız gelince yeni yazılan
  // karakterler eski response ile üzerine yazılıyordu. Sequence + AbortController ile önle.
  const patchSeqRef = useRef(0);
  const patchDraft = useCallback(async (updates) => {
    if (!draft) return;
    const seq = ++patchSeqRef.current;
    try {
      const { data } = await api.patch(`/copilot/product-draft/${draft.id}`, updates);
      // Sadece en son gönderilen isteğin yanıtı state'i güncellesin
      if (seq === patchSeqRef.current) setDraft(data);
    } catch (e) {
      if (seq === patchSeqRef.current) {
        toast.error(formatApiError(e) || "Taslak güncellenemedi");
      }
    }
  }, [draft]);

  // Commit — user pressed final confirm
  const commit = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const qs = saveTemplate && templateName.trim()
        ? `?save_as_template=true&template_name=${encodeURIComponent(templateName.trim())}`
        : "";
      const { data } = await api.post(`/copilot/product-draft/${draft.id}/commit${qs}`);
      toast.success(`${data.products_created ?? 1} ürün · ${data.variants_created} varyant · ${data.bom_ids?.length ?? 1} reçete oluşturuldu`);
      setConfirmOpen(false);
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Ürün oluşturulamadı");
    } finally {
      setSaving(false);
    }
  };

  // Discard — delete draft (no DB write happened)
  const discard = async () => {
    if (draft?.id) {
      try { await api.delete(`/copilot/product-draft/${draft.id}`); } catch {}
    }
    setDiscardConfirm(false);
    onClose?.();
  };

  const skuConflicts = useMemo(
    () => (draft?.variants || []).filter((v) => v.sku_conflict).length,
    [draft]
  );

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o && draft) {
            // If user tries to close with a draft, ask to discard first
            setDiscardConfirm(true);
          } else if (!o) {
            onClose?.();
          }
        }}
      >
        <DialogContent
          data-testid="ai-product-dialog"
          className="w-[95vw] max-w-[95vw] sm:max-w-3xl lg:max-w-5xl xl:max-w-6xl max-h-[92vh] flex flex-col p-0 gap-0 bg-slate-950 border-slate-800"
        >
          <DialogHeader className="px-5 py-4 border-b border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950">
            <DialogTitle className="flex items-center gap-2 text-slate-100">
              <div className="w-8 h-8 rounded-sm bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-sm font-bold tracking-tight">AI ile Ürün Oluştur</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                  Ön İzleme Zorunlu · DB'ye Yazılmaz · Onay Bekler
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {!draft && (
              <PromptStep prompt={prompt} setPrompt={setPrompt} busy={busy} onGenerate={generate} />
            )}

            {draft && (
              <PreviewStep
                draft={draft}
                setDraft={setDraft}
                patchDraft={patchDraft}
                skuConflicts={skuConflicts}
              />
            )}
          </div>

          <DialogFooter className="border-t border-slate-800 bg-slate-950/80 px-5 py-3 gap-2">
            {!draft ? (
              <>
                <Button variant="outline" onClick={() => onClose?.()} data-testid="ai-product-cancel-empty">
                  Kapat
                </Button>
                <Button
                  onClick={generate}
                  disabled={busy || !prompt.trim()}
                  className="bg-blue-600 hover:bg-blue-500 rounded-sm"
                  data-testid="ai-product-generate-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Taslağı Oluştur
                </Button>
              </>
            ) : (
              <>
                <div className="flex-1 flex items-center gap-2 text-[11px] text-slate-500">
                  <input
                    id="ai-save-template"
                    type="checkbox"
                    checked={saveTemplate}
                    onChange={(e) => setSaveTemplate(e.target.checked)}
                    data-testid="ai-save-template-check"
                  />
                  <label htmlFor="ai-save-template" className="cursor-pointer">Reçeteyi şablon olarak da kaydet</label>
                  {saveTemplate && (
                    <Input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="Şablon adı (ör: Polo Tişört Standart)"
                      className="h-7 max-w-[260px] bg-slate-900 border-slate-800 text-xs rounded-sm"
                      data-testid="ai-template-name-input"
                    />
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setDiscardConfirm(true)}
                  data-testid="ai-product-discard-btn"
                  className="rounded-sm border-slate-700"
                >
                  <X className="w-4 h-4 mr-1.5" /> Vazgeç
                </Button>
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={saving || skuConflicts > 0}
                  className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase text-xs font-semibold tracking-wider"
                  data-testid="ai-product-commit-btn"
                >
                  <Check className="w-4 h-4 mr-2" /> Onayla ve Ürünü Oluştur
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final commit confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="ai-product-confirm-dialog" className="bg-slate-950 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Ürünleri oluşturmak istiyor musunuz?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Bu işlem <b className="text-fuchsia-300">{(draft?.colors || []).length}</b> ayrı ürün kartı,
              toplam <b className="text-emerald-400">{draft?.variants?.length || 0}</b> beden varyantı,
              <b className="text-emerald-400"> {draft?.operations?.length || 0}</b> operasyon ve
              <b className="text-emerald-400"> {draft?.materials?.length || 0}</b> malzeme kaydı ile
              yeni ürün ailesi oluşturur. <span className="text-blue-300">Her renk için ayrı ürün kartı.</span> Bu işlem geri alınamaz (silmek için Ürünler sayfasını kullanın).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ai-product-confirm-cancel">İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={commit}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-500"
              data-testid="ai-product-confirm-ok"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Evet, Ürünü Oluştur
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Discard confirmation */}
      <AlertDialog open={discardConfirm} onOpenChange={setDiscardConfirm}>
        <AlertDialogContent className="bg-slate-950 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Taslağı silmek istiyor musunuz?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Hazırladığınız AI taslağı silinecek. DB'ye hiçbir ürün/varyant/reçete kaydı henüz yazılmadı.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={discard} className="bg-red-600 hover:bg-red-500" data-testid="ai-product-discard-ok">
              Evet, Taslağı Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* -----------------------  Adım 1: Prompt  ----------------------- */
function PromptStep({ prompt, setPrompt, busy, onGenerate }) {
  const examples = [
    "Kısa polo yaka tişört; Lacivert, Siyah, Beyaz renkler; S, M, L, XL, XXL bedenler. Kumaş: %100 pamuk lakoste. Operasyonlar: kesim (5₺), dikim (15₺), ütü (3₺).",
    "İş montu, kırmızı ve mavi; M, L, XL. Kumaş: softshell reflektörlü. Aksesuar: fermuar, cırt bant. Operasyonlar: kesim, dikim, kalite kontrol.",
    "Softshell mont; Antrasit, Siyah; S/M/L/XL/2XL; kumaş softshell 320gr; astar taffeta; fermuar YKK; operasyonlar kesim/dikim/kontrol.",
  ];
  return (
    <div className="space-y-4">
      <div className="rounded-sm border border-blue-900/40 bg-blue-950/20 p-3 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
        <div className="text-[11px] text-blue-200 leading-relaxed">
          Ürün, renk, beden, varyant SKU, reçete ve operasyonları Türkçe komuttan üretir.
          <b> Bilinmeyen fiyatlar/miktarlar uydurulmaz</b> — "Eksik bilgi" olarak işaretlenir.
          <br />Taslak <b>DB'ye yazılmaz</b>; siz onaylamadıkça hiçbir kalıcı işlem olmaz.
        </div>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-widest text-slate-500 font-mono mb-1.5 block">
          Ürün Açıklaması (Türkçe)
        </label>
        <Textarea
          data-testid="ai-product-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              onGenerate();
            }
          }}
          rows={5}
          placeholder="Örn: Kısa polo yaka tişört, Lacivert-Siyah-Beyaz, S/M/L/XL/XXL, kumaş: pamuk lakoste, operasyonlar: kesim/dikim/ütü…"
          className="bg-slate-900 border-slate-800 text-slate-100 placeholder:text-slate-600 rounded-sm text-sm"
        />
        <div className="text-[10px] text-slate-600 mt-1">Ctrl/⌘ + Enter ile hızlı üretim.</div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">
          Hızlı Örnekler
        </div>
        <div className="grid grid-cols-1 gap-1.5">
          {examples.map((ex, i) => (
            <button
              key={i}
              onClick={() => setPrompt(ex)}
              disabled={busy}
              className="text-left text-[11px] text-slate-400 hover:text-blue-300 hover:border-blue-700 border border-slate-800 bg-slate-900/40 px-2.5 py-2 rounded-sm transition-colors"
              data-testid={`ai-example-${i}`}
            >
              {ex}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -----------------------  Adım 2: Ön İzleme  ----------------------- */
function PreviewStep({ draft, setDraft, patchDraft, skuConflicts }) {
  const [pname, setPname] = useState(draft.product?.name || "");
  const [pcode, setPcode] = useState(draft.product?.product_code || "");
  const [colorsRaw, setColorsRaw] = useState((draft.colors || []).join(", "));
  const [sizesRaw, setSizesRaw] = useState((draft.sizes || []).join(", "));

  useEffect(() => {
    setPname(draft.product?.name || "");
    setPcode(draft.product?.product_code || "");
    setColorsRaw((draft.colors || []).join(", "));
    setSizesRaw((draft.sizes || []).join(", "));
  }, [draft.id]);

  const applyIdentity = async () => {
    await patchDraft({ product: { name: pname, product_code: pcode } });
  };
  const applyColorsSizes = async () => {
    const colors = colorsRaw.split(",").map((c) => c.trim()).filter(Boolean);
    const sizes = sizesRaw.split(",").map((c) => c.trim()).filter(Boolean);
    await patchDraft({ colors, sizes, regenerate_variants: true });
  };

  const updateVariant = (idx, patch) => {
    const next = draft.variants.map((v, i) => (i === idx ? { ...v, ...patch } : v));
    patchDraft({ variants: next });
  };
  const removeVariant = (idx) => {
    const next = draft.variants.filter((_, i) => i !== idx);
    patchDraft({ variants: next });
  };
  const suggestSku = async (idx) => {
    try {
      const { data } = await api.post(`/copilot/product-draft/${draft.id}/suggest-sku?variant_index=${idx}`);
      // Refresh draft to reflect server-side change
      const { data: fresh } = await api.get(`/copilot/product-draft/${draft.id}`);
      setDraft(fresh);
      toast.success(`Yeni benzersiz SKU önerildi: ${data.new_sku}`);
    } catch (e) {
      toast.error(formatApiError(e) || "SKU önerilemedi");
    }
  };

  const updateMaterial = (idx, patch) => {
    const next = (draft.materials || []).map((m, i) => (i === idx ? { ...m, ...patch } : m));
    patchDraft({ materials: next });
  };
  const addMaterial = () => {
    const next = [...(draft.materials || []), {
      name: "", kind: null, unit: null, qty: null, unit_cost: null,
      fire_percent: null, note: "",
      link_id: null, link_code: null, link_name: null, suggestions: [],
    }];
    patchDraft({ materials: next });
  };
  const removeMaterial = (idx) => patchDraft({ materials: (draft.materials || []).filter((_, i) => i !== idx) });

  const updateOp = (idx, patch) => {
    const next = (draft.operations || []).map((o, i) => (i === idx ? { ...o, ...patch } : o));
    patchDraft({ operations: next });
  };
  const addOp = () => patchDraft({ operations: [...(draft.operations || []), { name: "", unit_cost: null }] });
  const removeOp = (idx) => patchDraft({ operations: (draft.operations || []).filter((_, i) => i !== idx) });

  const cs = draft.cost_summary || {};

  return (
    <div className="space-y-4" data-testid="ai-product-preview">
      {/* --- Ürün Kimliği --- */}
      <section className="rounded-sm border border-slate-800 bg-slate-900/40 p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2 flex items-center gap-1.5">
          <Edit3 className="w-3 h-3" /> Ürün Kimliği
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Model Adı</label>
            <Input
              value={pname}
              onChange={(e) => setPname(e.target.value)}
              onBlur={applyIdentity}
              data-testid="ai-preview-name"
              className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm"
            />
            <div className="text-[9px] text-slate-500 mt-0.5">Nihai ürün adı: <b className="text-slate-300">{pname} (RENK)</b></div>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Model Kodu</label>
            <Input
              value={pcode}
              onChange={(e) => setPcode(e.target.value.toUpperCase())}
              onBlur={applyIdentity}
              data-testid="ai-preview-code"
              className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm font-mono"
            />
          </div>
          <div className="flex flex-col justify-end">
            <div className="text-[11px] text-slate-500 leading-tight">
              <span className="text-blue-300 font-semibold">📌 ERP Mimarisi:</span>
              <br/>Renk = <b className="text-slate-300">Ayrı ürün</b> · Beden = <b className="text-slate-300">Varyant</b>
            </div>
          </div>
        </div>
      </section>

      {/* --- Renk & Beden --- */}
      <section className="rounded-sm border border-slate-800 bg-slate-900/40 p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
          Renkler ve Bedenler <span className="text-blue-400">(değiştirince varyantlar yeniden üretilir)</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Renkler (virgülle)</label>
            <Input
              value={colorsRaw}
              onChange={(e) => setColorsRaw(e.target.value)}
              data-testid="ai-preview-colors"
              className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm"
              placeholder="Lacivert, Siyah, Beyaz"
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Bedenler (virgülle)</label>
            <Input
              value={sizesRaw}
              onChange={(e) => setSizesRaw(e.target.value)}
              data-testid="ai-preview-sizes"
              className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm"
              placeholder="S, M, L, XL, XXL"
            />
          </div>
        </div>
        <div className="mt-2">
          <Button
            size="sm"
            onClick={applyColorsSizes}
            className="rounded-sm bg-slate-800 hover:bg-slate-700 h-7 text-[11px]"
            data-testid="ai-preview-regenerate-btn"
          >
            <RefreshCw className="w-3 h-3 mr-1.5" /> Varyantları Yeniden Üret
          </Button>
        </div>
      </section>

      {/* --- Oluşturulacak Ürünler Özeti (renk bazlı) --- */}
      <section className="rounded-sm border border-fuchsia-900/40 bg-fuchsia-950/10 p-3" data-testid="ai-products-overview">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-mono mb-2">
          📦 Oluşturulacak Ürünler (her renk ayrı ürün kartı)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <Stat label="Ürün Sayısı" value={(draft.colors || []).length} highlight />
          <Stat label="Renk Sayısı" value={(draft.colors || []).length} />
          <Stat label="Beden Sayısı" value={(draft.sizes || []).length} />
          <Stat label="Toplam Varyant" value={draft.variants?.length || 0} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 max-h-48 overflow-y-auto">
          {(draft.colors || []).map((color, ci) => {
            const colorVariants = (draft.variants || []).filter(v => (v.color || v.attributes?.color) === color);
            const hasConflict = colorVariants.some(v => v.sku_conflict);
            return (
              <div
                key={color}
                data-testid={`ai-product-card-${ci}`}
                className={`p-2 rounded-sm border ${hasConflict ? "border-red-800 bg-red-950/20" : "border-slate-800 bg-slate-950/40"}`}
              >
                <div className="text-xs text-slate-100 font-semibold">
                  {pname || "Model"} <span className="text-blue-300">({trUpper(color)})</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5 font-mono">
                  {colorVariants.length} varyant · {colorVariants.map(v => v.sku).join(", ").slice(0, 100)}
                  {colorVariants.map(v => v.sku).join(", ").length > 100 ? "…" : ""}
                </div>
                {hasConflict && (
                  <div className="text-[10px] text-red-400 mt-0.5">⚠️ SKU çakışması var — aşağıdan düzelt</div>
                )}
              </div>
            );
          })}
          {(draft.colors || []).length === 0 && (
            <div className="text-[11px] text-slate-500 text-center py-2 col-span-full">Renk yok — yukarıdan ekleyin</div>
          )}
        </div>
      </section>

      {/* --- Varyantlar Tablosu --- */}
      <section className="rounded-sm border border-slate-800 bg-slate-900/40 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
            Varyantlar ({draft.variants?.length || 0})
          </div>
          {skuConflicts > 0 && (
            <Badge className="bg-red-950 text-red-300 border border-red-800 text-[10px]" data-testid="ai-sku-conflict-badge">
              {skuConflicts} SKU zaten mevcut — düzeltin
            </Badge>
          )}
        </div>
        <div className="max-h-64 overflow-y-auto border border-slate-800 rounded-sm">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-900/95">
              <TableRow className="border-slate-800">
                <TableHead className="text-[10px] text-slate-500 h-7">SKU</TableHead>
                <TableHead className="text-[10px] text-slate-500 h-7">Renk</TableHead>
                <TableHead className="text-[10px] text-slate-500 h-7">Beden</TableHead>
                <TableHead className="text-[10px] text-slate-500 h-7">Maliyet (₺)</TableHead>
                <TableHead className="text-[10px] text-slate-500 h-7">Satış (₺)</TableHead>
                <TableHead className="text-[10px] text-slate-500 h-7 w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(draft.variants || []).map((v, i) => (
                <TableRow key={i} className="border-slate-800" data-testid={`ai-variant-row-${i}`}>
                  <TableCell className="py-1">
                    <div className="flex items-center gap-1">
                      <DebouncedTextInput
                        value={v.sku || ""}
                        onCommit={(nv) => updateVariant(i, { sku: nv })}
                        upper
                        testId={`ai-variant-sku-${i}`}
                        className={`h-7 text-[11px] font-mono bg-slate-900 border-slate-800 rounded-sm flex-1 ${v.sku_conflict ? "border-red-700 text-red-300" : ""}`}
                      />
                      {v.sku_conflict && (
                        <button
                          onClick={() => suggestSku(i)}
                          className="text-[9px] px-1.5 py-1 bg-blue-950/60 hover:bg-blue-900 border border-blue-800 text-blue-200 rounded-sm whitespace-nowrap"
                          data-testid={`ai-variant-suggest-sku-${i}`}
                          title={`Bu SKU zaten mevcut (${(v.sku_conflict_reason || []).join(', ')}) — otomatik yeni SKU üret`}
                        >
                          Yeni SKU
                        </button>
                      )}
                    </div>
                    {v.sku_conflict && (
                      <div className="text-[9px] text-red-400 mt-0.5" data-testid={`ai-variant-conflict-msg-${i}`}>
                        {(v.sku_conflict_reason || []).includes("db")
                          ? "Sistemde zaten mevcut"
                          : "Taslak içinde tekrar ediyor"}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="py-1 text-xs text-slate-300">{v.color || v.attributes?.color || "—"}</TableCell>
                  <TableCell className="py-1 text-xs text-slate-300">{v.size || v.attributes?.size || "—"}</TableCell>
                  <TableCell className="py-1">
                    <DebouncedNumberInput
                      value={v.cost}
                      onCommit={(nv) => updateVariant(i, { cost: nv })}
                      testId={`ai-variant-cost-${i}`}
                      className="h-7 text-[11px] bg-slate-900 border-slate-800 rounded-sm w-24"
                    />
                  </TableCell>
                  <TableCell className="py-1">
                    <DebouncedNumberInput
                      value={v.sales_price}
                      onCommit={(nv) => updateVariant(i, { sales_price: nv })}
                      testId={`ai-variant-price-${i}`}
                      className="h-7 text-[11px] bg-slate-900 border-slate-800 rounded-sm w-24"
                    />
                  </TableCell>
                  <TableCell className="py-1">
                    <button
                      onClick={() => removeVariant(i)}
                      className="text-slate-500 hover:text-red-400 p-1"
                      title="Varyantı sil"
                      data-testid={`ai-variant-remove-${i}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
              {(draft.variants || []).length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-xs text-slate-500 py-4">Varyant yok</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* --- Reçete: Fiziksel Malzemeler (BOM) --- */}
      <section className="rounded-sm border border-slate-800 bg-slate-900/40 p-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Fiziksel Malzemeler (Ürün Reçetesi)</div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              Sadece 🟢 <b>stok kartına bağlı</b> malzemeler ürüne kaydedilir. Bağlanmamışlar taslak olarak kalır.
            </div>
          </div>
          <button onClick={addMaterial} className="text-[11px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1" data-testid="ai-material-add">
            <Plus className="w-3 h-3" /> Malzeme Ekle
          </button>
        </div>
        <div className="space-y-2">
          {(draft.materials || []).map((m, i) => (
            <MaterialRow
              key={i}
              index={i}
              material={m}
              updateMaterial={updateMaterial}
              removeMaterial={removeMaterial}
            />
          ))}
          {(draft.materials || []).length === 0 && <div className="text-[11px] text-slate-500 text-center py-2">Malzeme yok</div>}
        </div>
      </section>

      {/* --- Operasyonlar (STOKTAN DÜŞMEZ, sadece maliyet) --- */}
      <section className="rounded-sm border border-slate-800 bg-slate-900/40 p-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Operasyonlar / İşçilik</div>
            <div className="text-[10px] text-slate-500 mt-0.5">Kesim, dikim, ütü vb. — stok malzemesi değildir, sadece maliyet.</div>
          </div>
          <button onClick={addOp} className="text-[11px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1" data-testid="ai-operation-add">
            <Plus className="w-3 h-3" /> Operasyon Ekle
          </button>
        </div>
        <div className="space-y-1.5">
          {(draft.operations || []).map((o, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`ai-operation-row-${i}`}>
              <DebouncedTextInput value={o.name || ""} onCommit={(v) => updateOp(i, { name: v })} placeholder="Operasyon" className="col-span-9 h-7 text-[11px] bg-slate-900 border-slate-800 rounded-sm" testId={`ai-operation-name-${i}`} />
              <DebouncedNumberInput value={o.unit_cost} onCommit={(v) => updateOp(i, { unit_cost: v })} placeholder="Birim ₺" className={`col-span-2 h-7 text-[11px] bg-slate-900 border-slate-800 rounded-sm ${o.unit_cost == null ? "text-blue-400" : ""}`} testId={`ai-operation-cost-${i}`} />
              <button onClick={() => removeOp(i)} className="col-span-1 text-slate-500 hover:text-red-400 flex justify-center" data-testid={`ai-operation-remove-${i}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          {(draft.operations || []).length === 0 && <div className="text-[11px] text-slate-500 text-center py-2">Operasyon yok</div>}
        </div>
      </section>

      {/* --- Maliyet Özeti --- */}
      <section className="rounded-sm border border-slate-800 bg-slate-900/40 p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Maliyet Özeti</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
          <Stat label="Operasyon Top." value={`₺${Number(cs.operation_total || 0).toFixed(2)}`} />
          <Stat label="Malzeme Top." value={`₺${Number(cs.material_total || 0).toFixed(2)}`} />
          <Stat label="Eksik Op. Fiyat" value={cs.operation_missing_prices ?? 0} highlight={cs.operation_missing_prices > 0} />
          <Stat label="Eksik Mlz. Alanı" value={cs.material_missing_prices ?? 0} highlight={cs.material_missing_prices > 0} />
          <Stat label="Toplam (Tahmini)" value={cs.total_estimated == null ? "—" : `₺${Number(cs.total_estimated).toFixed(2)}`} />
        </div>
        {cs.note && (
          <div className="text-[11px] text-blue-300 mt-2 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" /> {cs.note}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div className={`rounded-sm border ${highlight ? "border-blue-800 bg-blue-950/30" : "border-slate-800 bg-slate-950/60"} p-2`}>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`text-sm font-bold ${highlight ? "text-blue-300" : "text-slate-100"} mt-0.5`}>{value}</div>
    </div>
  );
}

/**
 * MaterialRow — Reçete BOM satırı. Stok kartı bağlama akışını yönetir.
 * Kritik akış: kullanıcı [Stoktan Seç] veya [Yeni Stok Kartı] ile bağlar,
 * AI asla otomatik ID atamaz. Bağlanmamış satırlar 🔴 durumunda kalır ve
 * commit'te BOM'a fabric_line/accessory_line olarak yazılmaz (unlinked snapshot'ta kalır).
 */
function MaterialRow({ index, material, updateMaterial, removeMaterial }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newInitialName, setNewInitialName] = useState("");
  const linked = !!material.link_id;
  const kind = material.kind;

  const applyLink = (r) => {
    // r: {kind, id, code, name, unit, ...} — StockPicker veya NewStockCard'dan
    updateMaterial(index, {
      link_id: r.id,
      link_code: r.code,
      link_name: r.name,
      kind: r.kind,
      unit: material.unit || r.unit || (r.kind === "fabric" ? "kg" : "adet"),
      name: material.name || r.name,   // isim boşsa stok kartından al
    });
  };

  const unlink = () => updateMaterial(index, { link_id: null, link_code: null, link_name: null });

  return (
    <div
      data-testid={`ai-material-row-${index}`}
      className={`border rounded-sm p-2 ${linked ? "border-emerald-800 bg-emerald-950/10" : "border-slate-800 bg-slate-950/40"}`}
    >
      <div className="flex items-center gap-2 mb-2">
        {/* Kind badge */}
        <button
          onClick={() => updateMaterial(index, { kind: kind === "fabric" ? "accessory" : "fabric" })}
          className={`text-[9px] font-mono px-1.5 py-0.5 border rounded-sm uppercase tracking-wider ${
            kind === "fabric"
              ? "bg-cyan-900/40 text-cyan-300 border-cyan-800"
              : kind === "accessory"
              ? "bg-fuchsia-900/40 text-fuchsia-300 border-fuchsia-800"
              : "bg-slate-800 text-slate-400 border-slate-700"
          }`}
          data-testid={`ai-material-kind-${index}`}
          title="Kumaş ⇄ Aksesuar arasında geçiş yap"
        >
          {kind === "fabric" ? <Layers className="w-2.5 h-2.5 inline mr-1" /> : <Package className="w-2.5 h-2.5 inline mr-1" />}
          {kind === "fabric" ? "KUMAŞ" : kind === "accessory" ? "AKSESUAR" : "BELİRSİZ"}
        </button>
        {/* Link status */}
        {linked ? (
          <Badge className="bg-emerald-900/40 text-emerald-300 border border-emerald-700 text-[9px]" data-testid={`ai-material-linked-${index}`}>
            🟢 STOK KARTINA BAĞLI · {material.link_code || material.link_id?.slice(0, 6)}
          </Badge>
        ) : (
          <Badge className="bg-red-950 text-red-300 border border-red-800 text-[9px]" data-testid={`ai-material-unlinked-${index}`}>
            🔴 STOK KARTI SEÇİLMEDİ
          </Badge>
        )}
        <div className="flex-1" />
        {linked ? (
          <button
            onClick={unlink}
            className="text-[10px] text-slate-400 hover:text-red-400 flex items-center gap-1"
            data-testid={`ai-material-unlink-${index}`}
            title="Bağlantıyı kaldır"
          >
            <Unlink className="w-3 h-3" /> Bağlantıyı kaldır
          </button>
        ) : (
          <>
            <button
              onClick={() => setPickerOpen(true)}
              className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1 px-1.5 py-0.5 border border-blue-800 rounded-sm"
              data-testid={`ai-material-pick-${index}`}
            >
              <Search className="w-3 h-3" /> Stoktan Seç
            </button>
            <button
              onClick={() => { setNewInitialName(material.name || ""); setNewOpen(true); }}
              className="text-[10px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1 px-1.5 py-0.5 border border-emerald-800 rounded-sm"
              data-testid={`ai-material-new-${index}`}
            >
              <Plus className="w-3 h-3" /> Yeni Stok Kartı
            </button>
          </>
        )}
        <button
          onClick={() => removeMaterial(index)}
          className="text-slate-500 hover:text-red-400 p-1"
          data-testid={`ai-material-remove-${index}`}
          title="Malzeme sil"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {/* Ana satır — İsim / Birim / Miktar / Birim ₺ / Fire % */}
      <div className="grid grid-cols-12 gap-1.5 items-center">
        <div className="col-span-4">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">Malzeme Adı</div>
          <DebouncedTextInput
            value={material.name || ""}
            onCommit={(v) => updateMaterial(index, { name: v })}
            placeholder="Ör: Pamuk Lakoste Kumaş"
            className="h-7 text-[11px] bg-slate-900 border-slate-800 rounded-sm"
            testId={`ai-material-name-${index}`}
          />
        </div>
        <div className="col-span-2">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">Birim</div>
          <DebouncedTextInput
            value={material.unit || ""}
            onCommit={(v) => updateMaterial(index, { unit: v })}
            placeholder={kind === "fabric" ? "kg" : "adet"}
            className="h-7 text-[11px] bg-slate-900 border-slate-800 rounded-sm"
          />
        </div>
        <div className="col-span-2">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">Miktar / Adet</div>
          <DebouncedNumberInput
            value={material.qty}
            onCommit={(v) => updateMaterial(index, { qty: v })}
            placeholder="—"
            className={`h-7 text-[11px] bg-slate-900 border-slate-800 rounded-sm ${material.qty == null ? "text-blue-400 placeholder:text-blue-500" : ""}`}
            testId={`ai-material-qty-${index}`}
          />
        </div>
        <div className="col-span-2">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">Birim ₺</div>
          <DebouncedNumberInput
            value={material.unit_cost}
            onCommit={(v) => updateMaterial(index, { unit_cost: v })}
            placeholder="—"
            className={`h-7 text-[11px] bg-slate-900 border-slate-800 rounded-sm ${material.unit_cost == null ? "text-blue-400 placeholder:text-blue-500" : ""}`}
            testId={`ai-material-cost-${index}`}
          />
        </div>
        <div className="col-span-2">
          <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">Fire %</div>
          <DebouncedNumberInput
            value={material.fire_percent}
            onCommit={(v) => updateMaterial(index, { fire_percent: v })}
            placeholder="0"
            className="h-7 text-[11px] bg-slate-900 border-slate-800 rounded-sm"
            testId={`ai-material-fire-${index}`}
          />
        </div>
      </div>

      {/* AI önerileri — eğer bağlı değilse ve AI eşleşme bulmuşsa */}
      {!linked && (material.suggestions || []).length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-800">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono mb-1">🔍 AI Önerileri (tıkla, bağla)</div>
          <div className="flex flex-wrap gap-1">
            {material.suggestions.slice(0, 5).map((s, si) => (
              <button
                key={si}
                onClick={() => applyLink(s)}
                data-testid={`ai-material-suggest-${index}-${si}`}
                className="text-[10px] px-1.5 py-0.5 border border-slate-700 hover:border-blue-600 rounded-sm text-slate-300 hover:text-blue-300 transition-colors flex items-center gap-1"
              >
                {s.kind === "fabric" ? <Layers className="w-2.5 h-2.5 text-cyan-400" /> : <Package className="w-2.5 h-2.5 text-fuchsia-400" />}
                <span className="font-mono">{s.code}</span>
                <span className="truncate max-w-[220px]">{s.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <StockPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={applyLink}
        initialQuery={material.name || ""}
        onCreateNew={(q) => { setPickerOpen(false); setNewInitialName(q); setNewOpen(true); }}
      />
      <NewStockCardModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={applyLink}
        initialName={newInitialName || material.name || ""}
        initialKind={kind || "fabric"}
      />
    </div>
  );
}

/**
 * DebouncedNumberInput — race-safe numeric input.
 * BUG FIX (iter39): Kullanıcı hızlı yazdığında per-keystroke PATCH cevabı newer
 * karakterleri eziyordu. Yerel state tutar, blur veya 500ms debounce ile onCommit'i çağırır.
 */
function DebouncedNumberInput({ value, onCommit, className = "", placeholder = "—", testId }) {
  const [local, setLocal] = useState(value ?? "");
  const focusedRef = useRef(false);
  const timerRef = useRef(null);

  // Server'dan yeni değer geldiğinde SADECE odakta değilken senkronize et
  useEffect(() => {
    if (!focusedRef.current) setLocal(value ?? "");
  }, [value]);

  const commit = (v) => {
    const parsed = v === "" || v === null || v === undefined ? null : Number(v);
    if (parsed === value) return;
    onCommit(parsed);
  };

  return (
    <Input
      type="number"
      step="0.01"
      value={local}
      placeholder={placeholder}
      data-testid={testId}
      className={className}
      onFocus={() => { focusedRef.current = true; }}
      onChange={(e) => {
        setLocal(e.target.value);
        // Debounce PATCH (500 ms)
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => commit(e.target.value), 500);
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        commit(e.target.value);
      }}
    />
  );
}

/**
 * DebouncedTextInput — race-safe text input (same idea, for name/qty/sku fields).
 */
function DebouncedTextInput({ value, onCommit, className = "", placeholder = "", testId, upper = false }) {
  const [local, setLocal] = useState(value ?? "");
  const focusedRef = useRef(false);
  const timerRef = useRef(null);
  useEffect(() => { if (!focusedRef.current) setLocal(value ?? ""); }, [value]);
  const commit = (v) => {
    const normalized = upper ? String(v).toUpperCase() : v;
    if (normalized === value) return;
    onCommit(normalized);
  };
  return (
    <Input
      value={local}
      placeholder={placeholder}
      data-testid={testId}
      className={className}
      onFocus={() => { focusedRef.current = true; }}
      onChange={(e) => {
        const v = upper ? e.target.value.toUpperCase() : e.target.value;
        setLocal(v);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => commit(v), 500);
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        commit(upper ? e.target.value.toUpperCase() : e.target.value);
      }}
    />
  );
}
